import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useEffect, useRef, useState, Children } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { type Modifier, usePopper } from 'react-popper';

import { ProgressCircle } from '@dynatrace/strato-components/content';
import type { WithChildren } from '@dynatrace/strato-components/core';
import { Flex, Surface } from '@dynatrace/strato-components/layouts';

import { suggestionsLoading } from './Suggestion.sty.js';
import { Suggestion } from './Suggestion.js';
import { SuggestionGroup } from './SuggestionGroup.js';
import * as styles from './SuggestionsOverlay.sty.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import type { FilterFieldAutoSuggestionsContextProps } from '../contexts/FilterFieldContext.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';

function getSuggestionsContent(
  intl: IntlShape,
  autoSuggestions: FilterFieldAutoSuggestionsContextProps,
  children: FilterFieldSuggestionsProps['children'],
  optionVirtualizer: Virtualizer<HTMLDivElement, Element>,
) {
  const { suggestions, trailing } = autoSuggestions;

  const autosuggestionsAvailable = (suggestions?.length ?? 0) > 0;

  const childrenArray = Children.toArray(children);

  if (Children.toArray(children).length === 0 && !autosuggestionsAvailable) {
    return (
      <Suggestion
        trailing=""
        displayValue={intl.formatMessage({
          defaultMessage: 'No suggestions',
          id: 'PjDGLO7+f1/CNRR0',
          description: 'Filter field empty suggestions message.',
        })}
      />
    );
  }

  return (
    <>
      {childrenArray.length > 0 ? (
        <div
          style={{
            width: '100%',
            position: 'relative',
            height: optionVirtualizer.getTotalSize(),
            display: 'grid',
            gridTemplateRows: optionVirtualizer.measurementsCache
              .map((el) => `${el.size}px`)
              .join(' '),
          }}
          key={`wrapper-${childrenArray.length}`}
        >
          {optionVirtualizer.getVirtualItems().map((virtualRow) => {
            const child = childrenArray?.[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                className="strato-filter-field-suggestion"
                // data-index is required for the virtualizer to map the measure
                // element correctly to the measured index.
                data-index={virtualRow.index}
                style={{
                  gridRow: virtualRow.index + 1,
                  minWidth: 0,
                  height: 'fit-content',
                }}
                ref={(el) => {
                  optionVirtualizer.measureElement(el);
                }}
              >
                {child}
              </div>
            );
          })}
        </div>
      ) : null}
      {/*Render autosuggestions as being computed in suggestion extension*/}
      {/*and stated in suggestions callback.*/}
      {autosuggestionsAvailable && (
        <SuggestionGroup>
          {suggestions?.map((suggestion, index) => (
            <Suggestion
              // With the default "Use <input> as value" suggestion, there may be duplicate keys.
              // We're including the index for the key to avoid warnings.
              key={`${suggestion.value ?? 'non-interactive-'}${index}`}
              {...suggestion}
              trailing={suggestion.trailing ?? trailing}
            />
          ))}
        </SuggestionGroup>
      )}
    </>
  );
}

// Popper modifier to sync the max-height of the suggestions overlay
const heightModifier: Modifier<'filterfield-height-handler'> = {
  name: 'filterfield-height-handler',
  enabled: true,
  fn: ({ state }): void => {
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
    );

    const maxHeightBase = Math.max(viewportHeight / 2, 320);
    state.styles.popper.maxHeight = `${
      maxHeightBase - state.rects.reference.height - 20
    }px`;
  },
  phase: 'beforeWrite',
  requires: ['computeStyles'],
};

/**
 * Accepted properties for FilterFieldSuggestions.
 * @public
 */
export type FilterFieldSuggestionsProps = WithChildren & {
  /** Whether the suggestions are loading. If true, a loading indicator is shown and otherwise the suggestions are shown. */
  loading?: boolean;
};

/**
 * Compound component of the FilterField to apply suggestions.
 * @public
 */
export const Suggestions = (props: FilterFieldSuggestionsProps) => {
  const { loading, children }: FilterFieldSuggestionsProps = props;

  const intl = useIntl();

  const { filterFieldId, autoSuggestions, triggerRef } =
    useFilterFieldContext();

  const parentRef = useRef<HTMLDivElement | null>(null);
  // Get a setter on the parentRef to get notified when the element re-renders.
  const [notifyParentRefChange, setParentRefChange] =
    useState<HTMLDivElement | null>();

  const childrenArray = Children.toArray(children);

  const optionVirtualizer = useVirtualizer({
    count: childrenArray.length,
    getScrollElement: () => parentRef.current,
    overscan: 5,
    estimateSize: () => 32,
  });

  // When the parentElement re-renders, we will need to remeasure the virtualizer in order to
  // keep the correct height.
  useEffect(() => {
    requestAnimationFrame(() => {
      optionVirtualizer.measure();
    });
    // Deliberately leave optionVirtualizer out of the update deps to prevent a maximum update depth exceeded issue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyParentRefChange, childrenArray.length]);

  const suggestionsOverlayRef = useMergeRefs<HTMLDivElement | null>([
    parentRef,
    setParentRefChange,
  ]);

  const suggestionsRef = useRef<HTMLDivElement | null>(null);

  const { styles: positioningStyles } = usePopper(
    triggerRef,
    suggestionsRef.current,
    {
      placement: 'bottom-start',
      modifiers: [heightModifier],
    },
  );

  return (
    <Surface
      className={styles.suggestionsOverlay}
      elevation="floating"
      ref={suggestionsRef}
    >
      <div
        className={clsx(
          styles.suggestionsScrollContainer,
          'strato-filter-field-suggestions',
        )}
        ref={suggestionsOverlayRef}
        style={{ maxHeight: positioningStyles.popper.maxHeight }}
      >
        <ul
          className={styles.suggestionsList}
          {...{
            id: `suggestions-${filterFieldId}`,
            role: 'listbox',
            'aria-expanded': true,
            'aria-label': intl.formatMessage({
              id: 'BAfEyw7BhyJX3dfC',
              defaultMessage: 'Suggestions',
              description: 'Aria label for the suggestions overlay',
            }),
          }}
        >
          {loading ? (
            <Flex
              justifyContent="start"
              px={8}
              py={6}
              className={suggestionsLoading}
            >
              <ProgressCircle size="small" aria-label="Loading suggestions" />
              <FormattedMessage
                id="313HlKgYmTnqRqJU"
                defaultMessage="Loading suggestions"
                description="Message shown when suggestions are currently loaded."
              />
            </Flex>
          ) : (
            getSuggestionsContent(
              intl,
              autoSuggestions,
              children,
              optionVirtualizer,
            )
          )}
        </ul>
      </div>
    </Surface>
  );
};

Suggestions.displayValue = 'FilterField.Suggestions';
